import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ADMIN_EMAILS } from "../firebase";
import BrandLogo from "../components/BrandLogo";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      const dest = ADMIN_EMAILS.includes(email.trim()) ? "/admin/dashboard" : "/student/dashboard";
      navigate(dest);
    } catch (err) {
      toast.error(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: form */}
      <div className="flex-1 flex items-start justify-center bg-white p-6 sm:p-8">
        <div className="w-full max-w-md py-6 sm:py-10">
          <Link to="/" className="flex items-center gap-3 mb-10" data-testid="login-brand-link">
            <BrandLogo size="md" sub={false} />
          </Link>

          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Welcome back</h1>
          <p className="mt-2 text-slate-600">Login to access your student dashboard.</p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all disabled:opacity-50"
            >
              {loading ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-600 font-semibold hover:underline">Register now</Link>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Admin? <Link to="/admin/login" className="hover:underline">Use admin login</Link>
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:flex flex-1 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blue-600 rounded-full blur-[150px] opacity-30"></div>
        <div className="relative p-12 flex flex-col justify-between text-white">
          <Link to="/" className="text-sm text-slate-400 hover:text-white">← Back to home</Link>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tight max-w-md">
              Your internship journey continues here.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md">
              Access courses, track attendance, submit assignments, and download your certificate — all in one place.
            </p>
          </div>
          <div className="text-xs text-slate-500">© 2026 Kampus Campanion</div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

